import React, { useContext, useState } from "react";
import Box from "Components/Base/Layout/Box";
import { Typography } from "Components/Base/DataDisplay/Typography";
import { Stack } from "Components/Base/Feedback/Stack";
import { Skeleton } from "Components/Base/Feedback/Skeleton";
import { LearningActivityCard } from "Components/Module/LearningActivityItem";
import ActivityDescription from "Components/Module/LearningActivityItem/Description";
import {
  getCardTitle,
  formatCourseDuration,
  renderTimeDisplay,
  getCardImage,
} from "Components/Module/LearningActivityItem/functions";
import config from "lib/config";
import AddActivity from "domains/talentmanagement/coworker/goals/addactivity";
import { API_PROGRAM_ACTIVITY } from "lib/endpoints/programs";
import { sanitize } from "lib/sanitizer.js";
import { AddLinebreaksRemoveHtmlTags } from "lib/utils";
import { GET } from "Common/api";
import {
  useCourseCompletion,
  getDisplayDescription,
} from "../../../../functions";
import LangContext from "../../../../langContext";
import { STYLE_CARD_HEADING } from "../../style";
import { ActivityProgramChips } from "./CardChips";
import { ActivityProgramActions } from "./CardActions";
import {
  ACTIVITY_TYPE_PROGRAM_OTHER,
  ACTIVITY_TYPE_PROGRAM_COMMON,
} from "../../../../constants";

const { personalId } = config;

const fetchActivity = async (activityId) => {
  const activity =
    (await GET(API_PROGRAM_ACTIVITY, {
      activityId,
    })) || [];

  return activity;
};

const ActivityProgramCard = (props) => {
  const languageContext = useContext(LangContext);
  const { course, displayImage, isPortalOverview, isPreview, isManager } =
    props;

  const [completed, setCompleted] = useCourseCompletion(course);
  const [displayActivity, setDisplayActivity] = useState(false);
  const [activityDetails, setActivityDetails] = useState(null);
  const { description, shouldDisplay } = getDisplayDescription(
    course,
    isPortalOverview,
  );
  const displayTime = formatCourseDuration(course, languageContext);

  const toggleActivity = async (activityId, refreshOnly) => {
    if (!activityDetails || refreshOnly) {
      const fetchedActivityData = await fetchActivity(activityId);
      const activityData = {
        ...fetchedActivityData,
        id: activityId,
        courseId: fetchedActivityData.courseID,
        courseObjectId: fetchedActivityData.courseObjectID,
        type: fetchedActivityData.commonActivityID
          ? ACTIVITY_TYPE_PROGRAM_COMMON
          : ACTIVITY_TYPE_PROGRAM_OTHER,
      };

      setActivityDetails(activityData);
      setCompleted(activityData.isCompleted);
    }
    if (!refreshOnly) setDisplayActivity(true);
  };

  const onActivitySave = async () => {
    await toggleActivity(course.personalPlannedCoursesId, true);
    setDisplayActivity(false);

    if (props.onRefresh) {
      props.onRefresh();
    }
  };

  const onActivityReset = () => {
    setDisplayActivity(false);
  };

  const { imageComponent } = getCardImage(course, displayImage, false);

  const cardTitle = getCardTitle(course, isPortalOverview);

  const ActivityCardContent = () => {
    return (
      <>
        <Typography variant="h4" sx={STYLE_CARD_HEADING}>
          {languageContext("activity2")}
        </Typography>
        {shouldDisplay && (
          <ActivityDescription
            description={AddLinebreaksRemoveHtmlTags(sanitize(description))}
          />
        )}

        <Box
          display="flex"
          width="100%"
          justifyContent={displayTime ? "space-between" : "center"}
        >
          {renderTimeDisplay(displayTime)}
        </Box>

        {displayActivity && !activityDetails && (
          <Stack spacing={1}>
            <Skeleton variant="rectangular" width={210} height={60} />
          </Stack>
        )}

        {displayActivity && activityDetails && (
          <AddActivity
            activity={activityDetails}
            onShowSaveNotification={() => onActivitySave()}
            personalId={personalId}
            isManager={isManager}
            resetEditActivity={() => onActivityReset()}
            refreshOnClose={true}
            allowAttachment={true}
            fromProgramPage={true}
          />
        )}
      </>
    );
  };

  return (
    <LearningActivityCard
      id={`learningActivityCard${course.courseId}`}
      title={cardTitle}
      type={course.type}
      displayImage={displayImage}
      imageComponent={imageComponent}
      chips={
        <ActivityProgramChips
          course={course}
          completed={completed}
          languageContext={languageContext}
          isPortalOverview={isPortalOverview}
        />
      }
      content={<ActivityCardContent />}
      actions={
        <ActivityProgramActions
          course={course}
          isPreview={isPreview}
          languageContext={languageContext}
          onClick={() => toggleActivity(course.personalPlannedCoursesId, false)}
        />
      }
    />
  );
};

export default ActivityProgramCard;
